import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import FlippingCard from "./FlippingCard";

const faqs = [
  {
    question: "What is Prepairo?",
    answer:
      "Prepairo is an AI-powered exam preparation platform specifically designed for UPSC and other competitive exams. We offer personalized study plans, mock tests, and comprehensive study materials.",
    icon: "🎯",
  },
  {
    question: "How does the AI help in preparation?",
    answer:
      "Our AI analyzes your performance in mock tests and practice sessions to identify your strengths and weaknesses. It then suggests personalized learning paths and resources to help you improve efficiently.",
    icon: "🤖",
  },
  {
    question: "Is Prepairo available on mobile devices?",
    answer:
      "Yes, Prepairo has dedicated apps for both Android (Play Store) and iOS (App Store) devices, allowing you to study anytime, anywhere.",
    icon: "📱",
  },
  {
    question: "What kind of study materials are provided?",
    answer:
      "We provide a wide range of study materials including curated notes, current affairs updates, previous year question papers, and expert-authored articles, all structured for easy learning.",
    icon: "📚",
  },
  {
    question: "Can I track my progress?",
    answer:
      "Absolutely! Prepairo offers detailed progress tracking and analytics, so you can monitor your performance over time and see how you're improving in different subjects and topics.",
    icon: "📊",
  },
  {
    question: "How do I get started?",
    answer:
      "Getting started with Prepairo is easy! Simply create an account, choose your exam and start your preparation journey. Our AI will guide you through the process.",
    icon: "🚀",
  },
];

export function FaqSection() {
  const [activeCardIndex, setActiveCardIndex] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sectionRef.current && !sectionRef.current.contains(event.target)) {
        setActiveCardIndex(null);
      }
    };

    const handleScroll = () => {
      setActiveCardIndex(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleCardClick = (index) => {
    setActiveCardIndex(activeCardIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="py-16 lg:py-24 relative overflow-hidden"
      style={{ backgroundColor: "#f0f0f0" }}
      ref={sectionRef}
    >
      {/* Decorative Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />

        {/* Extra shapes */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          className="absolute top-1/4 left-10 w-40 h-40 bg-accent/15 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
          className="absolute bottom-1/4 right-10 w-52 h-52 bg-secondary/15 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Heading with Responsive Mascot */}
        <div className="flex flex-col sm:flex-row items-center justify-center text-center mb-12 gap-6 sm:gap-8">
          <motion.img
            src={`${process.env.PUBLIC_URL}/mascotFAQ.png`}
            alt="PrepAiro mascot"
            className="w-16 sm:w-20 lg:w-24 h-auto -mt-2 sm:mt-0"
            initial={{ opacity: 0, scale: 0.8, rotate: 15 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          />
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">
              Frequently Asked <span className="text-primary">Questions</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-xl mx-auto sm:mx-0">
              Find answers to common questions about Prepairo
            </p>
          </div>
        </div>

        {/* FAQ Grid */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {faqs.map((faq, index) => (
            <FlippingCard
              key={index}
              {...faq}
              isActive={activeCardIndex === index}
              onCardClick={() => handleCardClick(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
